import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Trophy, 
  Check, 
  Loader2, 
  Eye, 
  EyeOff,
  Shield,
  Sparkles,
  Clock,
  ArrowLeft,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { usePresenceTracking } from '@/hooks/usePresenceTracking';
import { validateName, formatName, validatePhone, formatPhone } from '@/utils/nameValidation';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  pink: '#df1283',
  orange: '#FF7901',
  cyan: '#0FFFE2',
  lime: '#B3FF00',
  gold: '#FFD700',
  goldDark: '#C5A547',
};

const FoundingMemberCheckout = () => {
  const navigate = useNavigate();
  const { setUser, login } = useAuth();
  
  // Track presence on signup page
  usePresenceTracking('signing_up');
  
  // Founding member availability
  const [isAvailable, setIsAvailable] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(true);
  
  // Checkout simulation states
  const [step, setStep] = useState('checking'); // 'checking' -> 'securing' -> 'form' -> 'creating'
  const [progress, setProgress] = useState(0);
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // UTM tracking
  const [utmData, setUtmData] = useState({});
  
  // Duplicate name detection - blocks signup
  const [duplicateNameError, setDuplicateNameError] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  
  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState({
    firstName: null,
    lastName: null,
    phoneNumber: null
  });

  // Check for duplicate name when both first and last name are filled
  const checkDuplicateName = async (first, last) => {
    if (!first || !last || first.length < 2 || last.length < 2) {
      setDuplicateNameError(null);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const response = await axios.post(`${API}/api/auth/check-duplicate-name`, {
        first_name: first,
        last_name: last
      });
      
      if (response.data.duplicate_found) {
        setDuplicateNameError(response.data.message);
      } else {
        setDuplicateNameError(null);
      }
    } catch (err) {
      console.error('Error checking duplicate name:', err);
      setDuplicateNameError(null);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Debounced duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstName && lastName) {
        checkDuplicateName(firstName, lastName);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [firstName, lastName]);

  // Capture UTM parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utm = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
    };
    // Filter out null values
    const filteredUtm = Object.fromEntries(
      Object.entries(utm).filter(([_, v]) => v !== null)
    );
    setUtmData(filteredUtm);
    // Also store in sessionStorage for Google OAuth flow
    if (Object.keys(filteredUtm).length > 0) {
      sessionStorage.setItem('utm_data', JSON.stringify(filteredUtm));
    }
  }, []);

  // Check if founding member is still available on mount
  useEffect(() => {
    const checkAvailability = async () => {
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        console.warn('Founding member availability check timed out');
        setCheckingAvailability(false);
        setStep('securing'); // Default to available on timeout
      }, 8000); // 8 second timeout
      
      try {
        const response = await axios.get(`${API}/api/auth/founding-member-status`, {
          timeout: 7000 // 7 second axios timeout
        });
        clearTimeout(timeoutId);
        setIsAvailable(response.data.available);
        if (response.data.available) {
          setStep('securing');
        } else {
          // Not available - set step to 'expired' to show the ended message
          setStep('expired');
        }
      } catch (err) {
        clearTimeout(timeoutId);
        console.error('Error checking founding member status:', err);
        // Default to available if check fails (better UX than blocking)
        setIsAvailable(true);
        setStep('securing');
      } finally {
        setCheckingAvailability(false);
      }
    };
    checkAvailability();
  }, []);

  // Simulate checkout experience
  useEffect(() => {
    let interval;
    if (step === 'securing') {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setStep('form');
            return 100;
          }
          return prev + 4;
        });
      }, 60);
    }
    return () => clearInterval(interval);
  }, [step]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setFieldErrors({ firstName: null, lastName: null, phoneNumber: null });
    setError('');
    
    // Validate names first
    const firstNameValidation = validateName(firstName, 'First name');
    const lastNameValidation = validateName(lastName, 'Last name');
    const phoneValidation = validatePhone(phoneNumber);
    
    if (!firstNameValidation.valid || !lastNameValidation.valid || !phoneValidation.valid) {
      setFieldErrors({
        firstName: firstNameValidation.error,
        lastName: lastNameValidation.error,
        phoneNumber: phoneValidation.error
      });
      // Show first error
      if (!firstNameValidation.valid) {
        setError(firstNameValidation.error);
      } else if (!lastNameValidation.valid) {
        setError(lastNameValidation.error);
      } else {
        setError(phoneValidation.error);
      }
      return;
    }
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Block signup if duplicate name exists
    if (duplicateNameError) {
      setError('An account with this name already exists. Please log in or reset your password.');
      return;
    }
    
    setSubmitting(true);
    setError('');
    setStep('creating');
    
    try {
      const response = await axios.post(`${API}/api/auth/founding-member-signup`, {
        email: email.toLowerCase().trim(),
        password: password,
        first_name: formatName(firstName),
        last_name: formatName(lastName),
        phone_number: formatPhone(phoneNumber),
        // Include UTM tracking data
        ...utmData
      }, { withCredentials: true });
      
      // Set user in auth context
      setUser(response.data);
      
      // Store session token
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      // Redirect to dashboard with welcome flag
      navigate('/dashboard?welcome=true&founding=true');
    } catch (err) {
      console.error('Founding member signup error:', err);
      setError(err.response?.data?.detail || 'Failed to create account. Please try again.');
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Google signup for founding members
  const handleGoogleSignup = () => {
    // Store a flag indicating this is a founding member signup
    sessionStorage.setItem('founding_member_google_signup', 'true');
    // Store UTM data for Google OAuth flow
    if (Object.keys(utmData).length > 0) {
      sessionStorage.setItem('utm_data', JSON.stringify(utmData));
    }
    // Use the auth context login which triggers Google OAuth
    login();
  };

  // Founding member expiration date and days remaining (fetched from API)
  const [expirationDate, setExpirationDate] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  
  // Format expiration date for user display - hardcoded for reliability
  const formatUserFacingExpiration = () => {
    return 'Ends August 10, 2026 at midnight EST';
  };
  
  // Fetch expiration info from API on mount
  useEffect(() => {
    const fetchExpiration = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/founding-member-status`);
        if (response.data.expiration_date) {
          const expDate = new Date(response.data.expiration_date);
          setExpirationDate(expDate);
          // Calculate days remaining based on user-facing date (subtract 2 days from technical date)
          // Use UTC to avoid timezone issues
          const utcYear = expDate.getUTCFullYear();
          const utcMonth = expDate.getUTCMonth();
          const utcDay = expDate.getUTCDate();
          const userFacingEndDate = new Date(Date.UTC(utcYear, utcMonth, utcDay - 2, 23, 59, 59));
          
          // Get current time and calculate days remaining
          const now = new Date();
          const diffMs = userFacingEndDate.getTime() - now.getTime();
          const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          setDaysRemaining(Math.max(0, diffDays));
        }
      } catch (err) {
        console.error('Error fetching expiration:', err);
      }
    };
    fetchExpiration();
  }, []);

  // Checking availability state
  if (step === 'checking' || checkingAvailability) {
    return (
      <div className="min-h-screen w-full bg-black flex items-center justify-center p-4 overflow-x-hidden">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: COLORS.gold }} />
          <p className="text-white/70">Checking availability...</p>
        </div>
      </div>
    );
  }

  // Expired state - founding member period has ended
  if (!isAvailable || step === 'expired') {
    return (
      <div className="min-h-screen w-full bg-black flex items-center justify-center p-4 overflow-x-hidden relative">
        <div className="hidden md:block absolute top-1/4 left-0 w-96 h-96 bg-[#df1283]/10 rounded-full blur-[150px] -translate-x-1/2 pointer-events-none" />
        <div className="hidden md:block absolute bottom-1/4 right-0 w-96 h-96 bg-[#FF7901]/10 rounded-full blur-[150px] translate-x-1/2 pointer-events-none" />
        
        <div className="relative z-10 max-w-md w-full text-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: `linear-gradient(135deg, ${COLORS.pink}30, ${COLORS.orange}30)` }}
          >
            <AlertCircle className="w-10 h-10" style={{ color: COLORS.orange }} />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            Founding Member Period Has Ended
          </h1>
          <p className="text-white/60 mb-8">
            The exclusive Founding Member offer has ended. 
            But don't worry - you can still join with one of our premium plans!
          </p>
          
          <button
            onClick={() => navigate('/#pricing')}
            className="px-8 py-4 rounded-full font-semibold text-black transition-all"
            style={{ 
              background: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.orange})`,
              boxShadow: `0 10px 30px ${COLORS.pink}30`
            }}
            data-testid="founding-expired-view-plans"
          >
            View Available Plans
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="block mx-auto mt-4 text-white/50 hover:text-white text-sm transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Securing your spot animation
  if (step === 'securing') {
    return (
      <div className="min-h-screen w-full bg-black flex items-center justify-center p-4 overflow-x-hidden relative">
        {/* Background Effects - hidden on mobile to prevent overflow */}
        <div className="hidden md:block absolute top-1/4 left-0 w-96 h-96 bg-[#C5A547]/10 rounded-full blur-[150px] -translate-x-1/2 pointer-events-none" />
        <div className="hidden md:block absolute bottom-1/4 right-0 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-[150px] translate-x-1/2 pointer-events-none" />
        
        <div className="relative z-10 max-w-md w-full text-center">
          <div 
            className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.goldDark}30, ${COLORS.gold}30)`,
              boxShadow: `0 0 60px ${COLORS.gold}30`
            }}
          >
            <Trophy className="w-12 h-12" style={{ color: COLORS.gold }} />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            Securing Your Founding Member Spot
          </h1>
          <p className="text-white/60 mb-8">
            Please wait while we reserve your exclusive membership...
          </p>
          
          {/* Progress Bar */}
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
            <div 
              data-testid="founding-securing-progress"
              className="h-full transition-all duration-100 ease-linear rounded-full"
              style={{ 
                width: `${progress}%`,
                background: `linear-gradient(90deg, ${COLORS.goldDark}, ${COLORS.gold})`
              }}
            />
          </div>
          
          <p className="text-sm text-white/40" data-testid="founding-securing-status">
            {progress < 30 && 'Validating offer...'}
            {progress >= 30 && progress < 60 && 'Checking availability...'}
            {progress >= 60 && progress < 90 && 'Reserving your spot...'}
            {progress >= 90 && 'Almost ready...'}
          </p>
        </div>
      </div>
    );
  }

  // Creating account animation
  if (step === 'creating') {
    return (
      <div className="min-h-screen w-full bg-black flex items-center justify-center p-4 overflow-x-hidden relative">
        <div className="hidden md:block absolute top-1/4 left-0 w-96 h-96 bg-[#C5A547]/10 rounded-full blur-[150px] -translate-x-1/2 pointer-events-none" />
        <div className="hidden md:block absolute bottom-1/4 right-0 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-[150px] translate-x-1/2 pointer-events-none" />
        
        <div className="relative z-10 text-center">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-6" style={{ color: COLORS.gold }} />
          <h2 className="text-2xl font-bold text-white mb-2" data-testid="founding-creating-title">Creating Your Account</h2>
          <p className="text-white/60">Setting up your Founding Member privileges...</p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen w-full bg-black flex items-center justify-center p-4 overflow-x-hidden relative">
      {/* Background Effects - completely hidden on small screens to prevent overflow */}
      <div className="hidden md:block absolute top-1/4 left-0 w-96 h-96 bg-[#C5A547]/10 rounded-full blur-[150px] -translate-x-1/2 pointer-events-none" />
      <div className="hidden md:block absolute bottom-1/4 right-0 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-[150px] translate-x-1/2 pointer-events-none" />
      
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 text-white/60 hover:text-white flex items-center gap-2 transition-colors z-50"
        data-testid="founding-back-button"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>
      
      <div className="relative z-10 max-w-lg w-full">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.goldDark}30, ${COLORS.gold}30)`,
              boxShadow: `0 0 40px ${COLORS.gold}20`
            }}
          >
            <Trophy className="w-10 h-10" style={{ color: COLORS.gold }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="founding-form-title">Your Spot is Reserved!</h1>
          <p className="text-white/60">
            Complete your account to claim your Founding Member status.
          </p>
        </div>

        {/* Order Summary Card */}
        <div 
          className="rounded-2xl p-6 mb-6"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.goldDark}15, ${COLORS.gold}10)`,
            border: `1px solid ${COLORS.goldDark}40`
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold" style={{ color: COLORS.gold }}>
                Founding Member
              </h3>
              <p className="text-sm text-white/50">Exclusive Early Access</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: COLORS.gold }}>FREE</p>
              <p className="text-xs text-white/40 line-through">$75/year</p>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Check className="w-4 h-4" style={{ color: COLORS.gold }} />
              <span>All Premium Features Unlocked</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Check className="w-4 h-4" style={{ color: COLORS.gold }} />
              <span>Exclusive Founding Member Badge</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Clock className="w-4 h-4" style={{ color: COLORS.gold }} />
              <span className="text-red-400 font-medium" data-testid="founding-expiration-message">
                {daysRemaining !== null
                  ? `${formatUserFacingExpiration()} (${daysRemaining} days remaining)`
                  : formatUserFacingExpiration()}
              </span>
            </div>
          </div>
        </div>

        {/* Account Creation Form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-6">Create Your Account</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="founding-email" className="block text-sm text-white/70 mb-2">Email Address</label>
              <input
                id="founding-email"
                data-testid="founding-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C5A547]"
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="founding-first-name" className="block text-sm text-white/70 mb-2">
                  First Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="founding-first-name"
                  data-testid="founding-first-name-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    // Real-time validation
                    if (e.target.value.length > 0) {
                      const result = validateName(e.target.value, 'First name');
                      setFieldErrors(prev => ({ ...prev, firstName: result.error }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, firstName: null }));
                    }
                  }}
                  onBlur={() => {
                    if (firstName) {
                      const result = validateName(firstName, 'First name');
                      setFieldErrors(prev => ({ ...prev, firstName: result.error }));
                    }
                  }}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C5A547] ${
                    fieldErrors.firstName ? 'border-red-500/70' : 'border-white/20'
                  }`}
                  placeholder="Your first name"
                  required
                />
                {fieldErrors.firstName && (
                  <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {fieldErrors.firstName}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="founding-last-name" className="block text-sm text-white/70 mb-2">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="founding-last-name"
                  data-testid="founding-last-name-input"
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    // Real-time validation
                    if (e.target.value.length > 0) {
                      const result = validateName(e.target.value, 'Last name');
                      setFieldErrors(prev => ({ ...prev, lastName: result.error }));
                    } else {
                      setFieldErrors(prev => ({ ...prev, lastName: null }));
                    }
                  }}
                  onBlur={() => {
                    if (lastName) {
                      const result = validateName(lastName, 'Last name');
                      setFieldErrors(prev => ({ ...prev, lastName: result.error }));
                    }
                  }}
                  className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C5A547] ${
                    fieldErrors.lastName ? 'border-red-500/70' : 'border-white/20'
                  }`}
                  placeholder="Your last name"
                  required
                />
                {fieldErrors.lastName && (
                  <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {fieldErrors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Duplicate Name Error - Blocks Signup */}
            {duplicateNameError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-red-200 text-sm font-medium mb-2">Account Already Exists</p>
                    <p className="text-red-200/80 text-sm">{duplicateNameError}</p>
                    <div className="mt-3 flex gap-3">
                      <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="text-sm text-[#0FFFE2] hover:underline"
                      >
                        Go to Login
                      </button>
                      <span className="text-white/30">|</span>
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="text-sm text-[#FF7901] hover:underline"
                      >
                        Reset Password
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Phone Number Field */}
            <div>
              <label htmlFor="founding-phone" className="block text-sm text-white/70 mb-2">
                Phone Number <span className="text-red-400">*</span>
              </label>
              <input
                id="founding-phone"
                data-testid="founding-phone-input"
                type="tel"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  // Real-time validation
                  if (e.target.value.length > 0) {
                    const result = validatePhone(e.target.value);
                    setFieldErrors(prev => ({ ...prev, phoneNumber: result.error }));
                  } else {
                    setFieldErrors(prev => ({ ...prev, phoneNumber: null }));
                  }
                }}
                onBlur={() => {
                  if (phoneNumber) {
                    const result = validatePhone(phoneNumber);
                    setFieldErrors(prev => ({ ...prev, phoneNumber: result.error }));
                  }
                }}
                className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C5A547] ${
                  fieldErrors.phoneNumber ? 'border-red-500/70' : 'border-white/20'
                }`}
                placeholder="Your phone number"
                required
              />
              {fieldErrors.phoneNumber && (
                <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {fieldErrors.phoneNumber}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="founding-password" className="block text-sm text-white/70 mb-2">Create Password <span className="text-red-400">*</span></label>
              <div className="relative">
                <input
                  id="founding-password"
                  data-testid="founding-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C5A547] pr-12"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
                <button
                  data-testid="founding-toggle-password-button"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="founding-confirm-password" className="block text-sm text-white/70 mb-2">Confirm Password</label>
              <input
                id="founding-confirm-password"
                data-testid="founding-confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C5A547]"
                placeholder="Confirm your password"
                required
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm" data-testid="founding-error-message">{error}</p>
            )}

            <button
              data-testid="founding-claim-button"
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-full font-semibold text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6"
              style={{ 
                background: `linear-gradient(90deg, ${COLORS.goldDark}, ${COLORS.gold})`,
                boxShadow: `0 10px 30px ${COLORS.goldDark}30`
              }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Claim My Founding Member Status
                </span>
              )}
            </button>
          </form>
          
          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-white/40 text-sm">or</span>
            <div className="flex-1 h-px bg-white/20" />
          </div>
          
          {/* Google Sign Up */}
          <button
            onClick={handleGoogleSignup}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-3 py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-xl transition-colors font-medium disabled:opacity-50"
            data-testid="founding-google-signup"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>
        </div>

        {/* Trust Indicators */}
        <div className="flex items-center justify-center gap-6 mt-6 text-sm text-white/40">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" style={{ color: COLORS.cyan }} />
            <span>Secure & Private</span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: COLORS.gold }} />
            <span>No Credit Card Required</span>
          </div>
        </div>

        <p className="text-center text-white/30 text-xs mt-6">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default FoundingMemberCheckout;
